import React, { useState, useEffect } from 'react';
import { Download, Pointer } from 'lucide-react';

interface InstallAppButtonProps {
  variant?: 'header-button' | 'card';
}

// Global variable to keep the prompt event across unmounts/remounts
let globalDeferredPrompt: any = null;

export const InstallAppButton: React.FC<InstallAppButtonProps> = ({ variant = 'card' }) => {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(globalDeferredPrompt);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if the app is already running in standalone mode
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    const handleBeforeInstallPrompt = (e: any) => {
      // Prevent Chrome 67 and earlier from automatically showing the prompt
      e.preventDefault();
      // Stash the event so it can be triggered later.
      globalDeferredPrompt = e;
      setDeferredPrompt(e);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      globalDeferredPrompt = null;
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Initial check just in case it fired before mount
    if (globalDeferredPrompt) {
      setDeferredPrompt(globalDeferredPrompt);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    
    // Show the install prompt
    deferredPrompt.prompt();
    
    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;
    
    // We've used the prompt, and can't use it again, discard it
    if (outcome === 'accepted') {
      globalDeferredPrompt = null;
      setDeferredPrompt(null);
    }
  };

  if (isInstalled || !deferredPrompt) {
    return null;
  }

  if (variant === 'header-button') {
    return (
      <button
        onClick={handleInstallClick}
        className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-500/20 transition-all font-bold text-xs border border-indigo-200 dark:border-indigo-500/30 shadow-sm"
        title="Install App"
      >
        <Download className="h-4 w-4" />
        <span>Install App</span>
      </button>
    );
  }

  return (
    <div className="flex flex-col items-center p-6 rounded-3xl bg-gradient-to-b from-indigo-50 to-white dark:from-indigo-950/40 dark:to-slate-900 border border-indigo-100 dark:border-indigo-800 shadow-xl w-full mb-4 relative overflow-hidden text-center">
      <style>{`
        @keyframes subtle-tap {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(-4px, -4px) scale(0.92); }
        }
        .animate-subtle-tap {
          animation: subtle-tap 2.5s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .animate-subtle-tap {
            animation: none;
          }
        }
      `}</style>
      
      {/* Subtle background glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-32 bg-indigo-400/20 dark:bg-indigo-600/20 blur-3xl rounded-full pointer-events-none" />

      <h3 className="text-xs sm:text-sm font-extrabold text-indigo-950 dark:text-indigo-100 uppercase tracking-widest mb-5 z-10 relative">
        DO YOU WANT TO STUDY SOMETHING?
      </h3>

      <div className="relative mb-6 z-10">
        <img 
          src="/MCQ_PULSE_LOGO519.png" 
          alt="MCQ PULSE" 
          className="w-24 h-24 rounded-2xl shadow-md object-cover bg-white p-1.5 mx-auto"
        />
        {/* Hand tapping logo */}
        <div className="absolute -bottom-3 -right-3 text-indigo-600 dark:text-indigo-400 drop-shadow-lg animate-subtle-tap">
          <Pointer className="w-10 h-10 fill-indigo-100 dark:fill-indigo-900 stroke-[1.5]" />
        </div>
      </div>

      <p className="text-base font-bold text-slate-800 dark:text-slate-100 mb-1 z-10 relative">
        Where every question sparks success
      </p>
      
      <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-6 z-10 relative">
        Want a better way to study? Install now.
      </p>

      <button
        onClick={handleInstallClick}
        className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm shadow-md transition-all active:scale-95 z-10 relative"
      >
        <Download className="h-5 w-5" />
        <span>INSTALL APP</span>
      </button>
    </div>
  );
};
